import type { Http } from '../http';
import { endpoints } from '../endpoints';
import type { RequestOptions } from '../types/common';
import type { RawManualOrderSimpleResponse } from '../types/manual-node';
import type { RawOrderResponse } from '../types/order';
import type {
  RawCancelReceiptRequest,
  RawCancelReceiptResponse,
  RawPlantReceiptsResponse,
  RawUpdateReceiptManualRequest,
  RawUpdateReceiptRequest,
} from '../types/receipt';

export function createReceiptApi(http: Http) {
  return {
    /** GET /api/v2/plants/{plantId}/receipts — 발전소 영수증 목록 조회 */
    async listByPlant(
      plantId: number,
      params?: { page?: number; size?: number },
      options?: RequestOptions,
    ): Promise<RawPlantReceiptsResponse> {
      const res = await http.get<RawPlantReceiptsResponse>(endpoints.receipt.byPlant(plantId), {
        params,
        signal: options?.signal,
      });
      return res.data;
    },

    /**
     * GET /api/v2/receipts/{receiptId} — 영수증 단건 조회.
     * 응답 = 주문/영수증 통합 구조. `include` 로 billing·subscription 확장(콤마 구분).
     */
    async getById(
      receiptId: number,
      params?: { include?: string },
      options?: RequestOptions,
    ): Promise<RawOrderResponse> {
      const res = await http.get<RawOrderResponse>(endpoints.receipt.byId(receiptId), {
        params,
        signal: options?.signal,
      });
      return res.data;
    },

    /** PATCH /api/v2/receipts/{receiptId} — 영수증 메모 수정 (무통장입금 외 주문) */
    async updateById(
      receiptId: number,
      data: RawUpdateReceiptRequest,
    ): Promise<RawManualOrderSimpleResponse> {
      const res = await http.patch<RawManualOrderSimpleResponse>(
        endpoints.receipt.byId(receiptId),
        data,
      );
      return res.data;
    },

    /**
     * DELETE /api/v2/receipts/{receiptId} — 영수증(주문) 취소. **전액 취소만** 지원(`cancelAmount` ≠ 잔액이면 400).
     * axios delete 는 body 를 config 로 넘긴다(`data`) — 이 엔드포인트는 요청 body 가 required 다.
     */
    async cancel(
      receiptId: number,
      data: RawCancelReceiptRequest,
    ): Promise<RawCancelReceiptResponse> {
      const res = await http.delete<RawCancelReceiptResponse>(endpoints.receipt.cancel(receiptId), {
        data,
      });
      return res.data;
    },

    /** PATCH /api/v2/receipts/{receiptId}/manual — 무통장입금 영수증 수정 */
    async updateManual(
      receiptId: number,
      data: RawUpdateReceiptManualRequest,
    ): Promise<RawManualOrderSimpleResponse> {
      const res = await http.patch<RawManualOrderSimpleResponse>(
        endpoints.receipt.manual(receiptId),
        data,
      );
      return res.data;
    },
  };
}
